import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  CalendarDays,
  Download,
  RefreshCw,
  RotateCcw,
  Sparkles,
  Upload,
} from 'lucide-react'
import exmclDatesheetBuilderService, {
  type DatesheetBuilderPayload,
  type BuilderPaper,
} from '@/services/exmclDatesheetBuilderService'
import { usePermissions } from '@/hooks/usePermissions'
import { errorMessage } from '@/components/exmcl/ExmclUi'
import {
  Banner,
  Button,
  Card,
  EmptyState,
  Page,
  StatCard,
  Stepper,
  Switch,
  TextField,
  formatDate,
} from '@/components/ui'
import '@/components/exmcl/exmcl.css'
import '@/components/exmcl/datesheet-builder.css'

type UndoEntry = Array<{ id: string; date: string | null }>

const formatDayLabel = (date: string) => {
  try {
    return formatDate(date)
  } catch {
    return date
  }
}

const ExmclDatesheetBuilder: React.FC = () => {
  const { examId = '' } = useParams<{ examId: string }>()
  const navigate = useNavigate()
  const canWrite = usePermissions().can('exam.setup.write')
  const [payload, setPayload] = useState<DatesheetBuilderPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [selectedPaperId, setSelectedPaperId] = useState<string | null>(null)
  const [undoStack, setUndoStack] = useState<UndoEntry[]>([])
  const [rulesDraft, setRulesDraft] = useState<DatesheetBuilderPayload['scheduleRules'] | null>(null)
  const [windowDraft, setWindowDraft] = useState({ start: '', end: '', sittingStart: '09:00', sittingEnd: '12:00' })

  const load = useCallback(async () => {
    if (!examId) return
    setLoading(true)
    try {
      const data = await exmclDatesheetBuilderService.get(examId)
      setPayload(data)
      setRulesDraft(data.scheduleRules)
      setWindowDraft({
        start: data.exam.scheduleStartDate || '',
        end: data.exam.scheduleEndDate || '',
        sittingStart: data.exam.sittingStart || '09:00',
        sittingEnd: data.exam.sittingEnd || '12:00',
      })
    } catch (error) {
      toast.error(errorMessage(error, 'Date sheet didn’t load.'))
      navigate('/exmcl/datesheets', { replace: true })
    } finally {
      setLoading(false)
    }
  }, [examId, navigate])

  useEffect(() => {
    void load()
  }, [load])

  const pushUndo = (papers: BuilderPaper[]) => {
    setUndoStack((prev) => [...prev.slice(-29), papers.map((p) => ({ id: p.id, date: p.date }))])
  }

  const applyPayload = (data: DatesheetBuilderPayload) => {
    setPayload(data)
    setRulesDraft(data.scheduleRules)
    setWindowDraft({
      start: data.exam.scheduleStartDate || '',
      end: data.exam.scheduleEndDate || '',
      sittingStart: data.exam.sittingStart || '09:00',
      sittingEnd: data.exam.sittingEnd || '12:00',
    })
  }

  const paperByClassDate = useMemo(() => {
    const map = new Map<string, BuilderPaper>()
    payload?.papers.forEach((p) => {
      if (p.date) map.set(`${p.className}::${p.date}`, p)
    })
    return map
  }, [payload?.papers])

  const unscheduled = useMemo(
    () => (payload?.papers || []).filter((p) => !p.date),
    [payload?.papers]
  )

  const handleCellClick = async (className: string, date: string, kind: string) => {
    if (!canWrite || !payload || kind !== 'open') return
    if (!selectedPaperId) {
      const existing = paperByClassDate.get(`${className}::${date}`)
      if (existing) setSelectedPaperId(existing.id)
      return
    }
    const moving = payload.papers.find((p) => p.id === selectedPaperId)
    if (!moving || moving.className !== className) {
      toast.error('Select a paper for this class first.')
      return
    }
    setBusy(true)
    pushUndo(payload.papers)
    try {
      const data = await exmclDatesheetBuilderService.patchPaper(examId, selectedPaperId, date)
      applyPayload(data)
      setSelectedPaperId(null)
    } catch (error) {
      toast.error(errorMessage(error, 'Couldn’t place that paper.'))
    } finally {
      setBusy(false)
    }
  }

  const handleUndo = async () => {
    if (!canWrite || !payload || undoStack.length === 0) return
    const snapshot = undoStack[undoStack.length - 1]
    setUndoStack((prev) => prev.slice(0, -1))
    setBusy(true)
    try {
      let next = payload
      for (const row of snapshot) {
        next = await exmclDatesheetBuilderService.patchPaper(examId, row.id, row.date)
      }
      applyPayload(next)
    } catch (error) {
      toast.error(errorMessage(error, 'Undo failed.'))
    } finally {
      setBusy(false)
    }
  }

  const saveRules = async () => {
    if (!canWrite || !rulesDraft) return
    setBusy(true)
    try {
      const data = await exmclDatesheetBuilderService.putRules(examId, {
        scheduleRules: rulesDraft,
        scheduleStartDate: windowDraft.start || undefined,
        scheduleEndDate: windowDraft.end || undefined,
        sittingStart: windowDraft.sittingStart,
        sittingEnd: windowDraft.sittingEnd,
      })
      applyPayload(data)
      toast.success('Schedule settings saved.')
    } catch (error) {
      toast.error(errorMessage(error, 'Couldn’t save settings.'))
    } finally {
      setBusy(false)
    }
  }

  const runAutoArrange = async () => {
    if (!canWrite) return
    if (payload) pushUndo(payload.papers)
    setBusy(true)
    try {
      const data = await exmclDatesheetBuilderService.autoArrange(examId)
      applyPayload(data)
      if (data.unplacedCount) {
        toast.error(`${data.unplacedCount} paper(s) could not be placed — widen the window or relax rules.`)
      } else {
        toast.success('Papers auto-arranged.')
      }
    } catch (error) {
      toast.error(errorMessage(error, 'Auto-arrange failed.'))
    } finally {
      setBusy(false)
    }
  }

  const runPublish = async () => {
    if (!canWrite) return
    setBusy(true)
    try {
      const data = await exmclDatesheetBuilderService.publish(examId)
      applyPayload(data)
      toast.success('Date sheet published.')
    } catch (error) {
      toast.error(errorMessage(error, 'Publish failed.'))
    } finally {
      setBusy(false)
    }
  }

  const runSync = async () => {
    if (!canWrite) return
    setBusy(true)
    try {
      const data = await exmclDatesheetBuilderService.syncPapers(examId)
      applyPayload(data)
      toast.success('Papers synced from subjects.')
    } catch (error) {
      toast.error(errorMessage(error, 'Sync failed.'))
    } finally {
      setBusy(false)
    }
  }

  const suggestHolidays = async () => {
    if (!canWrite) return
    try {
      const rows = await exmclDatesheetBuilderService.suggestHolidays(examId)
      if (!rows.length) {
        toast('No school holidays found in this window.')
        return
      }
      setRulesDraft((prev) => {
        const base = prev || payload?.scheduleRules
        if (!base) return prev
        const seen = new Set(base.holidays.map((h) => h.date))
        const merged = [...base.holidays]
        rows.forEach((row) => {
          if (!seen.has(row.date)) merged.push(row)
        })
        return { ...base, holidays: merged.sort((a, b) => a.date.localeCompare(b.date)) }
      })
      toast.success('Holidays added from the school calendar — save settings to apply.')
    } catch (error) {
      toast.error(errorMessage(error, 'Couldn’t load holiday suggestions.'))
    }
  }

  if (loading || !payload) {
    return (
      <Page className="ex-dsb-page">
        <EmptyState icon={CalendarDays} title="Loading date sheet…" />
      </Page>
    )
  }

  const { exam, stats, dayRows, classes } = payload
  const published = exam.scheduleStatus === 'published'
  const hasClashes = stats.clashCount > 0

  return (
    <Page className="ex-dsb-page has-mobile-foot">
      <div className="ex-dsb-stats">
        <StatCard label="Papers scheduled" value={`${stats.scheduledCount} / ${stats.totalPapers}`} />
        <StatCard label="Exam days" value={String(stats.examDays)} />
        <StatCard
          label="Status"
          value={published ? 'Published' : 'Draft'}
          hint={published && exam.schedulePublishedAt ? formatDate(exam.schedulePublishedAt) : undefined}
        />
      </div>

      {hasClashes ? (
        <Banner tone="warning" title="Scheduling clashes">
          <p>{`${stats.clashCount} paper(s) are too close together for the same class.`}</p>
          {canWrite ? (
            <Button size="sm" icon={Sparkles} onClick={() => void runAutoArrange()} loading={busy}>
              Auto-arrange
            </Button>
          ) : null}
        </Banner>
      ) : null}

      {!windowDraft.start || !windowDraft.end ? (
        <Banner tone="info" title="Set the exam window">
          Choose start and end dates in Schedule settings, then place papers on the grid.
        </Banner>
      ) : null}

      <div className="ex-dsb-layout">
        <Card
          flush
          title="Schedule grid"
          subtitle={`${windowDraft.sittingStart}–${windowDraft.sittingEnd} · click a paper, then a day`}
          action={
            <div className="ex-exams-headtools">
              {canWrite ? (
                <>
                  <Button size="sm" icon={RotateCcw} disabled={!undoStack.length} onClick={() => void handleUndo()}>
                    Undo
                  </Button>
                  <Button size="sm" icon={Sparkles} onClick={() => void runAutoArrange()} loading={busy}>
                    Auto-arrange
                  </Button>
                </>
              ) : null}
              <Button size="sm" icon={Download} onClick={() => toast('PDF export is coming soon.')}>
                PDF
              </Button>
            </div>
          }
        >
          {dayRows.length === 0 ? (
            <EmptyState
              compact
              icon={CalendarDays}
              title="No days in the window"
              body="Set start and end dates under Schedule settings, then save."
            />
          ) : (
            <div className="ex-dsb-grid-wrap">
              <table className="ex-dsb-grid">
                <caption className="sr-only">Exam schedule by class and date</caption>
                <thead>
                  <tr>
                    <th scope="col">Date</th>
                    {classes.map((cls) => (
                      <th key={cls} scope="col">
                        Class {cls}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dayRows.map((row) => (
                    <tr key={row.date} className={row.kind !== 'open' ? 'is-off' : undefined}>
                      <th scope="row" className="ex-dsb-day">
                        {row.kind === 'open' ? formatDayLabel(row.date) : row.label || row.date}
                      </th>
                      {classes.map((cls) => {
                        if (row.kind !== 'open') {
                          return <td key={cls} aria-hidden="true" />
                        }
                        const paper = paperByClassDate.get(`${cls}::${row.date}`)
                        return (
                          <td key={cls}>
                            <div
                              className="ex-dsb-cell"
                              role="button"
                              tabIndex={0}
                              onClick={() => void handleCellClick(cls, row.date, row.kind)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault()
                                  void handleCellClick(cls, row.date, row.kind)
                                }
                              }}
                            >
                              {paper ? (
                                <span
                                  className={`ex-dsb-chip${paper.clash ? ' is-clash' : ''}${
                                    selectedPaperId === paper.id ? ' is-active' : ''
                                  }`}
                                >
                                  {paper.subjectName || paper.subjectCode}
                                </span>
                              ) : null}
                            </div>
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="ex-dsb-side">
          <Card title="Unscheduled" subtitle={`${unscheduled.length} paper(s) without a date`}>
            {unscheduled.length === 0 ? (
              <EmptyState compact title="All papers have dates" />
            ) : (
              <div className="ex-dsb-unscheduled">
                {unscheduled.map((paper) => (
                  <button
                    key={paper.id}
                    type="button"
                    className={`ex-dsb-chip${selectedPaperId === paper.id ? ' is-active' : ''}`}
                    onClick={() => setSelectedPaperId(selectedPaperId === paper.id ? null : paper.id)}
                  >
                    {paper.className} · {paper.subjectName || paper.subjectCode}
                  </button>
                ))}
              </div>
            )}
          </Card>

          <Card
            title="Schedule settings"
            subtitle="Exam window, sitting time and spacing rules"
            footer={
              canWrite ? (
                <Button variant="primary" loading={busy} onClick={() => void saveRules()}>
                  Save settings
                </Button>
              ) : undefined
            }
          >
            <div className="cb-stack">
              <TextField
                label="Window start"
                type="date"
                value={windowDraft.start}
                onChange={(e) => setWindowDraft((w) => ({ ...w, start: e.target.value }))}
                disabled={!canWrite}
              />
              <TextField
                label="Window end"
                type="date"
                value={windowDraft.end}
                onChange={(e) => setWindowDraft((w) => ({ ...w, end: e.target.value }))}
                disabled={!canWrite}
              />
              <TextField
                label="Sitting starts"
                type="time"
                value={windowDraft.sittingStart}
                onChange={(e) => setWindowDraft((w) => ({ ...w, sittingStart: e.target.value }))}
                disabled={!canWrite}
              />
              <TextField
                label="Sitting ends"
                type="time"
                value={windowDraft.sittingEnd}
                onChange={(e) => setWindowDraft((w) => ({ ...w, sittingEnd: e.target.value }))}
                disabled={!canWrite}
              />
              <Switch
                label="Skip Sundays"
                checked={rulesDraft?.skipSundays !== false}
                onChange={(checked) => setRulesDraft((r) => (r ? { ...r, skipSundays: checked } : r))}
                disabled={!canWrite}
              />
              <Stepper
                label="Minimum free days between papers (same class)"
                value={rulesDraft?.minFreeDaysBetweenPapers ?? 1}
                min={0}
                max={3}
                onChange={(value) =>
                  setRulesDraft((r) => (r ? { ...r, minFreeDaysBetweenPapers: value } : r))
                }
                disabled={!canWrite}
              />
              {canWrite ? (
                <Button size="sm" variant="secondary" onClick={() => void suggestHolidays()}>
                  Add holidays from school calendar
                </Button>
              ) : null}
              {(rulesDraft?.holidays || []).map((holiday, index) => (
                <div key={`${holiday.date}-${index}`} className="ex-dsb-holiday-row">
                  <TextField
                    label="Holiday date"
                    type="date"
                    value={holiday.date}
                    onChange={(e) =>
                      setRulesDraft((r) => {
                        if (!r) return r
                        const holidays = [...r.holidays]
                        holidays[index] = { ...holidays[index], date: e.target.value }
                        return { ...r, holidays }
                      })
                    }
                    disabled={!canWrite}
                  />
                  <TextField
                    label="Name"
                    value={holiday.name}
                    onChange={(e) =>
                      setRulesDraft((r) => {
                        if (!r) return r
                        const holidays = [...r.holidays]
                        holidays[index] = { ...holidays[index], name: e.target.value }
                        return { ...r, holidays }
                      })
                    }
                    disabled={!canWrite}
                  />
                </div>
              ))}
              {canWrite ? (
                <Button
                  size="sm"
                  onClick={() =>
                    setRulesDraft((r) =>
                      r ? { ...r, holidays: [...r.holidays, { date: windowDraft.start || '', name: '' }] } : r
                    )
                  }
                >
                  Add holiday
                </Button>
              ) : null}
            </div>
          </Card>

          {canWrite ? (
            <Card title="Subjects" subtitle="Sync papers from the exam–subject matrix">
              <Button icon={RefreshCw} onClick={() => void runSync()} loading={busy}>
                Sync papers
              </Button>
            </Card>
          ) : null}
        </div>
      </div>

      {canWrite ? (
        <div className="ex-dsb-foot">
          <Button variant="secondary" onClick={() => navigate('/exmcl/datesheets')}>
            Back
          </Button>
          <Button variant="primary" icon={Upload} loading={busy} onClick={() => void runPublish()} disabled={published}>
            {published ? 'Published' : 'Publish'}
          </Button>
        </div>
      ) : null}
    </Page>
  )
}

export default ExmclDatesheetBuilder
